import { Components, Theme, ThemeOptions, alpha } from '@mui/material/styles';
import { SizeMap } from '@/themes';

const getCheckboxTheme = (theme: Theme): ThemeOptions => {
  // Checkbox Size Map
  // Can be overridden by defining theme.size.checkbox in muiTheme.ts
  // Example: size: { checkbox: { s: { fontSize: '24px' }, ... } }
  const checkboxSize: SizeMap = {
    s: { fontSize: '24px' },
    m: { fontSize: '26px' },
    l: { fontSize: '28px' },
    xl: { fontSize: '32px' },
  };

  const checkboxTheme: Components<Theme>['MuiCheckbox'] = {
    defaultProps: {
      size: 's',
      disableRipple: true,
    },

    styleOverrides: {
      root: {
        padding: 0,
        '&:hover': {
          backgroundColor: 'transparent',
        },
        borderRadius: '100%',
      },
    },

    variants: [
      // ─────────────────────────────
      // SIZE VARIANTS
      // ─────────────────────────────
      {
        props: { size: 's' },
        style: {
          '& .MuiSvgIcon-root': {
            ...checkboxSize.s, // Spread the size config
          },
        },
      },
      {
        props: { size: 'm' },
        style: {
          '& .MuiSvgIcon-root': {
            ...checkboxSize.m, // Spread the size config
          },
        },
      },
      {
        props: { size: 'l' },
        style: {
          '& .MuiSvgIcon-root': {
            ...checkboxSize.l, // Spread the size config
          },
        },
      },
      {
        props: { size: 'xl' },
        style: {
          '& .MuiSvgIcon-root': {
            ...checkboxSize.xl, // Spread the size config
          },
        },
      },

      // ─────────────────────────────
      // PRIMARY COLOR
      // ─────────────────────────────
      {
        props: { color: 'primary' },
        style: {
          color: theme.palette.M3.sys.light['on-surface-variant'],
          '&.Mui-checked': {
            color: theme.palette.M3.sys.light.primary,
          },
          '&:hover': {
            backgroundColor:
              theme.palette.M3['state-layers'].light['primary']['opacity-0.08'],
          },
          // MUI Checkbox does not have a focused and pressed states.
          '&.Mui-disabled': {
            color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
          },
        },
      },

      // ─────────────────────────────
      // DESTRUCTIVE COLOR
      // ─────────────────────────────
      {
        props: { color: 'destructive' },
        style: {
          color: theme.palette.State.error,
          '&.Mui-checked': {
            color: theme.palette.State.error,
          },
          '&:hover': {
            backgroundColor:
              theme.palette.M3['state-layers'].light['error']['opacity-0.08'],
          },
          // MUI Checkbox does not have a focused and pressed states.
          '&.Mui-disabled': {
            color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
          },
        },
      },
    ],
  };

  return {
    components: { MuiCheckbox: checkboxTheme },
    size: { checkbox: checkboxSize },
  };
};

export default getCheckboxTheme;
